import {ExternalLinkIcon} from '@heroicons/react/outline';
import type {ReactNode} from 'react';
import {useEffect, useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {
  Badge,
  Button,
  Link,
  PermissionsProvider,
  TableSkeleton,
} from '@/components';
import {liveAccessService} from '@/services/liveAccessService';
import type {BusinessLiveAccessListItem, BusinessLiveAccessView} from '@/types';
import {BusinessLiveAccessStatus} from '@/types';
import * as formatDate from '@/utils/date';
import {getErrorMessage} from '@/utils/errorMessage';
import {renderFile} from '@/utils/imageFiles';
import {logRejection} from '@/utils/logRejection';
import {showErrorMessage} from '@/utils/showErrorMessage';

import {liveAccessStatusColorScheme} from './liveAccessStatus';
import RejectLiveAccessModal from './RejectLiveAccessModal';
import {useLiveAccessActions} from './useLiveAccessActions';

const SummaryRow = ({label, value}: {label: string; value: ReactNode}) => (
  <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
    <p className="flex-[0.4] text-slate-500 text-sm dark:text-slate-100">
      {label}
    </p>
    <div className="flex-[0.6] text-sm font-medium text-gray-700 dark:text-gray-100">
      {value ?? '-'}
    </div>
  </div>
);

// Renders a set of signed getObject URLs the way RFI does: a titled section with
// renderFile() previews (PDF embed + "View PDF" link, or image) in a wrap grid.
const DocumentSection = ({
  title,
  documents,
  emptyLabel,
  dataCy,
}: {
  title: string;
  documents: Record<string, string>;
  emptyLabel: string;
  dataCy: string;
}) => {
  const entries = Object.entries(documents ?? {});
  return (
    <div className="mt-6" data-cy={dataCy}>
      <h4 className="font-semibold text-gray-900 dark:text-gray-300 mb-2">
        {title.toLocaleUpperCase()}
      </h4>
      <hr className="mb-3 dark:border-dark-borderMid" />
      <div className="flex flex-wrap gap-1">
        {entries.length > 0 ? (
          entries.map(([docType, url]) => (
            <div
              key={docType}
              className="w-1/4 pr-2"
              data-cy="live-access-document"
              data-document-type={docType}
            >
              {renderFile(url, docType)}
            </div>
          ))
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {emptyLabel}
          </p>
        )}
      </div>
    </div>
  );
};

interface LiveAccessSummaryProps {
  item: BusinessLiveAccessListItem;
  onChanged: () => void;
}

const LiveAccessSummary = ({item, onChanged}: LiveAccessSummaryProps) => {
  const [view, setView] = useState<BusinessLiveAccessView | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showReject, setShowReject] = useState(false);

  const load = async () => {
    try {
      setIsLoading(true);
      setView(await liveAccessService.getById(item.id));
    } catch (e) {
      showErrorMessage(getErrorMessage(e, 'Error loading request'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // `load` toasts its own failures, so this only fires if that catch or the
    // finally itself throws. Log rather than swallow silently.
    load().catch(logRejection('Failed to load live access request'));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load is intentionally omitted; only refetch when item.id changes
  }, [item.id]);

  const {isApproving, isRejecting, approve, reject} = useLiveAccessActions(
    item,
    () => {
      setShowReject(false);
      // Post-action refresh. Same as the mount load: `load` toasts its own
      // failures, so this only fires if that handler itself throws.
      load().catch(logRejection('Failed to reload live access request'));
      onChanged();
    },
  );

  const isTerminal =
    view?.status === BusinessLiveAccessStatus.Approved ||
    view?.status === BusinessLiveAccessStatus.Rejected;
  // Older requests carry the final agreement an admin uploaded to them; it is
  // shown read only. Agreements are managed on the business's Agreement tab.
  const hasRequestAgreement =
    !!view && Object.keys(view.signedFinalDocuments ?? {}).length > 0;
  const agreementLink = `/merchants/businesses/${item.businessId}?tab=agreement`;

  const handleApprove = () => {
    // `approve` toasts its own failures, so this only fires if that catch or
    // the finally itself throws. Log rather than swallow silently.
    approve().catch(logRejection('Failed to approve live access request'));
  };

  return (
    <div
      className="bg-white dark:bg-transparent rounded-lg w-full px-5 py-5"
      data-cy="live-access-summary"
    >
      {isLoading || !view ? (
        <TableSkeleton />
      ) : (
        <>
          <div className="flex items-center mb-3 gap-10">
            <div className="flex items-center gap-4">
              <h2 className="font-semibold text-xl mr-3">
                Live Access Request
              </h2>
              <Badge
                label={view.status}
                colorScheme={liveAccessStatusColorScheme(view.status)}
                dataCy="live-access-summary-status"
              />
            </div>

            {!isTerminal && (
              <PermissionsProvider
                permission={AfriexAdminPermission.BusinessLiveAccessUpdate}
              >
                <div className="flex items-center flex-wrap gap-2">
                  <Button
                    colorScheme="cyan"
                    className="text-xs px-3 py-1 h-8"
                    isLoading={isApproving}
                    loadingText="Approving..."
                    dataCy="live-access-approve"
                    disabled={!view.hasFinalAgreement}
                    onClick={handleApprove}
                  >
                    Approve
                  </Button>
                  <Button
                    colorScheme="red"
                    className="text-xs px-3 py-1 h-8"
                    dataCy="live-access-reject"
                    onClick={() => setShowReject(true)}
                  >
                    Reject
                  </Button>
                </div>
              </PermissionsProvider>
            )}
          </div>

          <SummaryRow label="Business Name" value={item.businessName} />
          <SummaryRow label="Owner Email" value={item.ownerEmail} />
          <SummaryRow
            label="Agreement"
            value={
              <Link
                to={agreementLink}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
                data-cy="live-access-agreement-link"
              >
                <Button
                  variant="ghost"
                  className="no-padding no-margin"
                  rightIcon={<ExternalLinkIcon className="w-4 h-4" />}
                >
                  {view.hasFinalAgreement ? 'In place' : 'Not in place yet'}
                </Button>
              </Link>
            }
          />
          {!view.hasFinalAgreement && !isTerminal && (
            <div className="mb-2 py-2 px-3 text-sm bg-yellow-50 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200">
              This business has no agreement in place yet, so it cannot be
              approved. Its agreement is on the business&apos;s Agreement tab.
            </div>
          )}
          <SummaryRow
            label="Requested On"
            value={formatDate.DDMonYYYY(view.createdAt || '')}
          />
          {isTerminal && view.decidedAt && (
            <SummaryRow
              label="Decided On"
              value={formatDate.DDMonYYYY(view.decidedAt)}
            />
          )}
          <DocumentSection
            title="Submitted Documents"
            documents={view.signedSubmittedDocuments}
            emptyLabel="No documents uploaded yet."
            dataCy="live-access-submitted-documents"
          />
          {hasRequestAgreement && (
            <DocumentSection
              title="Signed Final Agreement"
              documents={view.signedFinalDocuments}
              emptyLabel=""
              dataCy="live-access-final-documents"
            />
          )}
        </>
      )}

      <RejectLiveAccessModal
        isOpen={showReject}
        isLoading={isRejecting}
        onClose={() => setShowReject(false)}
        onSubmit={reject}
      />
    </div>
  );
};

export default LiveAccessSummary;
